import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import type { Incident } from '../../services/incidentsApi';
import type { IncidentPolicyContextItem } from '../../pages/incidents/incidentDisplayModel';
import {
  incidentCanonicalEvidenceReadinessLabel,
  incidentCanonicalEscalationReasons,
  incidentCanonicalEscalationState,
  incidentCanonicalFollowUpStateLabel,
  incidentCanonicalFollowupOwnerLabel,
  incidentCanonicalNextReviewAt,
  incidentCanonicalOperatorNote,
  incidentCanonicalProofScopeId,
  incidentCanonicalRuleOwnerLabel,
  incidentCanonicalReviewOutcomeLabel,
  incidentCanonicalCameraCalibrationLabel,
  incidentCanonicalEvidenceQualityLabel,
  incidentCanonicalMeasurementBasisLabel,
  incidentCanonicalSourceContextLabel,
} from './reviewCanonical';
import {
  displaySafetyManagerLabel,
  formatActionOwnerChipLabel,
} from '../../domain/safetyManagerUxLabels';

export interface IncidentSummaryCardProps {
  selectedIncident: Incident | null;
  workflowLabel: string;
  opsStateLabel: string;
  explanation: string;
  currentUser?: string | null;
  policyContext?: IncidentPolicyContextItem[];
}

const formatWhen = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const IncidentSummaryCard: React.FC<IncidentSummaryCardProps> = ({
  selectedIncident,
  workflowLabel,
  opsStateLabel,
  explanation,
  currentUser,
  policyContext = [],
}) => {
  const followupOwner = incidentCanonicalFollowupOwnerLabel(selectedIncident) || 'Unassigned';
  const ruleOwner = incidentCanonicalRuleOwnerLabel(selectedIncident);
  const assignedAt = formatWhen(
    (selectedIncident as any)?.safety_case?.ownership?.assigned_at || selectedIncident?.assigned_at,
  );
  const assignedBy = String(
    (selectedIncident as any)?.safety_case?.ownership?.assigned_by || selectedIncident?.assigned_by || followupOwner,
  ).trim();
  const reviewOutcomeLabel = incidentCanonicalReviewOutcomeLabel(selectedIncident);
  const evidenceReadinessLabel = incidentCanonicalEvidenceReadinessLabel(selectedIncident);
  const proofScopeId = incidentCanonicalProofScopeId(selectedIncident);
  const operatorNote = incidentCanonicalOperatorNote(selectedIncident);
  const followUpStateLabel = incidentCanonicalFollowUpStateLabel(selectedIncident);
  const escalationState = incidentCanonicalEscalationState(selectedIncident);
  const escalationReasons = incidentCanonicalEscalationReasons(selectedIncident);
  const nextReviewAt = incidentCanonicalNextReviewAt(selectedIncident);
  const sourceContextLabel = incidentCanonicalSourceContextLabel(selectedIncident);
  const evidenceQualityLabel = incidentCanonicalEvidenceQualityLabel(selectedIncident);
  const measurementBasisLabel = incidentCanonicalMeasurementBasisLabel(selectedIncident);
  const cameraCalibrationLabel = incidentCanonicalCameraCalibrationLabel(selectedIncident);
  const ownerLabel = formatActionOwnerChipLabel({ owner: followupOwner, currentUser });
  const workflowContext =
    selectedIncident?.status === 'ACKED'
      ? `Ownership confirmed${assignedAt !== '—' ? ` at ${assignedAt}` : ''}`
      : followUpStateLabel
        ? followUpStateLabel
      : selectedIncident?.status === 'IN_REVIEW'
        ? 'Evidence review is in progress'
        : escalationState === 'overdue' || selectedIncident?.ops_overdue
          ? 'Follow-up is overdue'
          : 'Needs initial review';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.1,
        mb: 2,
        borderRadius: 3,
        borderColor: '#d0d7e2',
        bgcolor: '#fbfcfe',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Typography variant="overline" sx={{ fontSize: 10.5, letterSpacing: 1, color: 'text.secondary', display: 'block', fontWeight: 800 }}>
        Incident summary
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.8, mb: 1.2 }}>
        <Chip size="small" variant="outlined" color="default" label={`Follow-up: ${opsStateLabel || 'Needs review'}`} sx={{ fontWeight: 800 }} />
        <Chip size="small" variant="outlined" color="default" label={`Review status: ${workflowLabel || 'Needs review'}`} sx={{ fontWeight: 800 }} />
        <Chip size="small" variant="outlined" color={followupOwner === 'Unassigned' ? 'default' : 'primary'} label={ownerLabel} sx={{ fontWeight: 800 }} />
        {ruleOwner ? (
          <Chip size="small" variant="outlined" color="default" label={`Rule owner: ${ruleOwner}`} sx={{ fontWeight: 800 }} />
        ) : null}
        {reviewOutcomeLabel ? (
          <Chip size="small" variant="outlined" color="default" label={`Outcome: ${reviewOutcomeLabel}`} sx={{ fontWeight: 800 }} />
        ) : null}
        {evidenceReadinessLabel ? (
          <Chip size="small" variant="outlined" color={evidenceReadinessLabel === 'Ready' ? 'success' : 'default'} label={`Evidence: ${evidenceReadinessLabel}`} sx={{ fontWeight: 800 }} />
        ) : null}
        {proofScopeId ? (
          <Chip size="small" variant="outlined" color="default" label={`Scope: ${proofScopeId}`} sx={{ fontWeight: 800 }} />
        ) : null}
        {evidenceQualityLabel ? (
          <Chip size="small" variant="outlined" color="default" label={`${displaySafetyManagerLabel('evidence_quality')}: ${evidenceQualityLabel}`} sx={{ fontWeight: 800 }} />
        ) : null}
        {measurementBasisLabel ? (
          <Chip size="small" variant="outlined" color="default" label={`${displaySafetyManagerLabel('measurement_basis')}: ${measurementBasisLabel}`} sx={{ fontWeight: 800 }} />
        ) : null}
        {cameraCalibrationLabel ? (
          <Chip
            size="small"
            variant="outlined"
            color={cameraCalibrationLabel === 'Floor-plane calibrated' ? 'success' : 'default'}
            label={`Calibration: ${cameraCalibrationLabel}`}
            sx={{ fontWeight: 800 }}
          />
        ) : null}
      </Box>

      <Box sx={{ display: 'grid', gap: 0.65 }}>
        <Typography variant="body1" sx={{ fontWeight: 800, lineHeight: 1.6 }}>
          {explanation}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
          {workflowContext}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
          Opened {formatWhen(selectedIncident?.created_at)} · Updated {formatWhen(selectedIncident?.updated_at)}
        </Typography>
        {(assignedBy || assignedAt !== '—') && followupOwner !== 'Unassigned' && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            Ownership set by {assignedBy} {assignedAt !== '—' ? `at ${assignedAt}` : ''}
          </Typography>
        )}
        {sourceContextLabel && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            Source context: {sourceContextLabel}
          </Typography>
        )}
        {nextReviewAt && (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            Target follow-up: {formatWhen(nextReviewAt)}
          </Typography>
        )}
        {operatorNote ? (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            Review note: {operatorNote}
          </Typography>
        ) : null}
        {escalationReasons.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            Escalation signals: {String(escalationReasons.join(', '))}
          </Typography>
        ) : null}
      </Box>

      {policyContext.length > 0 && (
        <Box sx={{ mt: 1.6 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.75, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.35 }}
          >
            Policy context
          </Typography>
          <Box sx={{ display: 'grid', gap: 0.45 }}>
            {policyContext.slice(0, 4).map((item) => (
              <Typography key={`${item.label}:${item.value}`} variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                <Box component="span" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  {item.label}:
                </Box>{' '}
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {item.value}
                </Box>
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default IncidentSummaryCard;
